import { useMemo } from 'react';

// Deep import: the tenant-profile barrel pulls in its screens, which import
// back into shared feature code. Only this hook is wanted here — and owning the
// workaround once is half the reason this file exists.
import { useActiveTemplateWithValues } from '../../tenant-profile-hub/api/tenant-profile-template-api';
import { useRemediationRunItems } from '../api/remediation-api';
import { collectPlaceholders, type PendingPlaceholder } from '../utils/placeholders';

export interface RunPlaceholders {
  /** The run's review items, for callers that also need the documents themselves. */
  items: ReturnType<typeof useRemediationRunItems>['data'];
  /** One entry per distinct token across the run, matched to a profile field. */
  placeholders: PendingPlaceholder[];
  isLoading: boolean;
}

/**
 * The blanks a run still carries, assembled once.
 *
 * Two screens ask this question — the fill step, and the summary that warns
 * before finalizing — and each was wiring up the same four parts by hand: the
 * items query, the profile template, `collectPlaceholders`, and the deep-import
 * workaround above, comment included. A workaround re-explained in every file it
 * appears in is one that wants an owner.
 *
 * `enabled` is not decoration. `useRemediationRunItems` returns every item's
 * full document plus its raw `sourceText` in one response — the heaviest read in
 * the feature — so a caller that only wants a count should say when the count
 * stops being worth the payload.
 */
export function useRunPlaceholders(
  runId: string | undefined,
  enabled = true,
): RunPlaceholders {
  const { data: items, isLoading: itemsLoading } = useRemediationRunItems(runId, enabled);
  const { data: template, isLoading: templateLoading } = useActiveTemplateWithValues();

  // Deliberately not keyed on the user's answers. Collecting rebuilds the whole
  // field-lookup map — a regex per template key and label — and which blanks
  // exist does not change as they are filled in.
  const placeholders = useMemo(
    () => (items && template ? collectPlaceholders(items.items ?? [], template) : []),
    [items, template],
  );

  return { items, placeholders, isLoading: itemsLoading || templateLoading };
}
